import { Menu, Transition } from '@headlessui/react';
import {
  Bars3Icon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  // XMarkIcon,
} from '@heroicons/react/24/outline';
import React, { Fragment, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { SearchFieldScope } from './SearchFieldScope';
import { NAV_FEATURE_SEARCH_FIELDS } from '../constants/searchFieldScopes';
import { useAuth } from '../features/auth/useAuth';
import { usePersistedSearchFields } from '../hooks/usePersistedSearchFields';
import i18n from '../i18n';
import { AppBrand } from './AppBrand';
import UserInfo from './UserInfo';

interface LayoutProps {
  children: React.ReactNode;
}

type NavItem = {
  name: string;
  href: string;
  icon: string;
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { logout, canAccessPath } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');
  const [navSearch, setNavSearch] = useState('');
  const deferredNavSearch = useDeferredValue(navSearch);
  const {
    searchFields: navSearchFields,
    toggleField: toggleNavSearchField,
    allFields: navSearchAllFields,
  } = usePersistedSearchFields('navFeatures', NAV_FEATURE_SEARCH_FIELDS);

  // Listen for language changes to update UI
  useEffect(() => {
    const handleLanguageChanged = (lng: string) => {
      setCurrentLang(lng);
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const navigation = useMemo(
    () =>
      [
        { name: t('navigation.dashboard'), href: '/dashboard', icon: '📊' },
        { name: t('navigation.recoveryDashboard'), href: '/reports/recovery-dashboard', icon: '📊' },
        { name: t('navigation.customers'), href: '/customers', icon: '👥' },
        { name: t('navigation.items'), href: '/items', icon: '📦' },
        { name: t('navigation.brands'), href: '/brands', icon: '🏷️' },
        { name: t('navigation.bundles'), href: '/bundles', icon: '📦' },
        { name: t('navigation.stores'), href: '/stores', icon: '🏪' },
        { name: t('navigation.posOutlets'), href: '/pos/outlets', icon: '🏬' },
        { name: t('navigation.posTerminals'), href: '/pos/terminals', icon: '🖥️' },
        { name: t('navigation.posAdjustments'), href: '/pos/stock-adjustments', icon: '📉' },
        { name: t('navigation.posSales'), href: '/pos/sales', icon: '🧾' },
        { name: t('navigation.thermalPrinter'), href: '/thermal-printer', icon: '🖨️' },
        { name: t('navigation.routes'), href: '/routes', icon: '🗺️' },
        { name: t('navigation.vehicles'), href: '/vehicles', icon: '🚗' },
        { name: t('navigation.trip'), href: '/trip', icon: '🚚' },
        { name: t('navigation.deliveryPackages'), href: '/delivery-packages', icon: '📮' },
        { name: t('navigation.payments'), href: '/payments', icon: '💳' },
        { name: t('navigation.grn'), href: '/grn', icon: '↩️' },
        { name: t('navigation.returnToSupplier'), href: '/return-to-supplier', icon: '📤' },
        { name: t('navigation.replacementNote'), href: '/replacement-note', icon: '🔄' },
        { name: t('navigation.serviceReceipt'), href: '/service-receipt', icon: '🧾' },
        { name: t('navigation.remuneration'), href: '/remuneration', icon: '💰' },
        { name: t('navigation.expenses'), href: '/expenses', icon: '🧾' },
        { name: t('navigation.enquiries'), href: '/enquiries', icon: '📋' },
        { name: t('navigation.serviceTickets'), href: '/service-tickets', icon: '🎫' },
        { name: t('navigation.samples'), href: '/samples', icon: '🧪' },
        { name: t('navigation.sourceDocuments'), href: '/source-documents', icon: '📄' },
        { name: t('navigation.zeettu'), href: '/zeettu', icon: '💳' },
        { name: t('navigation.installmentCards'), href: '/installment-cards', icon: '💳' },
        { name: t('navigation.purchaseStock'), href: '/purchase-stock', icon: '🛒' },
        { name: t('navigation.storeStock'), href: '/store-stock', icon: '🏬' },
        { name: t('navigation.purchaseReports'), href: '/purchase-reports', icon: '📑' },
        { name: t('navigation.zdr'), href: '/reports/zdr', icon: '📋' },
        { name: t('navigation.pdr'), href: '/reports/pdr', icon: '📋' },
        { name: t('navigation.irr'), href: '/reports/irr', icon: '💰' },
        { name: t('navigation.str'), href: '/reports/str', icon: '📦' },
        { name: t('navigation.employees'), href: '/employees', icon: '👨‍💼' },
        { name: t('navigation.sellers'), href: '/sellers', icon: '🤝' },
        { name: t('navigation.audit'), href: '/audit', icon: '🔍' },
        { name: t('navigation.settings'), href: '/settings', icon: '⚙️' },
      ].filter((item) => canAccessPath(item.href)),
    [t, canAccessPath],
  );

  const filteredNavigation = useMemo(() => {
    const q = deferredNavSearch.trim().toLowerCase();
    if (!q) return navigation;
    const searchName = navSearchFields.includes('name');
    const searchPath = navSearchFields.includes('path');
    return navigation.filter((item) => {
      if (searchName && item.name.toLowerCase().includes(q)) return true;
      if (searchPath && item.href.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [navigation, deferredNavSearch, navSearchFields]);

  const isNavActive = (href: string) =>
    location.pathname === href ||
    (href.length > 1 && location.pathname.startsWith(`${href}/`));

  const languages = [
    { code: 'en', name: t('languages.en') },
    { code: 'si', name: t('languages.si') },
    { code: 'ta', name: t('languages.ta') },
  ];

  const currentLanguage = currentLang;
  const currentLanguageName = languages.find(lang => lang.code === currentLanguage)?.name || t('languages.en');

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng).catch((error) => {
      console.error('Error changing language:', error);
    });
  };

  const searchPlaceholder = t('navigation.searchFeatures') || 'Search features...';
  const noFeaturesFound = t('navigation.noFeaturesFound') || 'No features found';

  const renderNavLinks = (items: NavItem[], onNavigate?: () => void) => {
    if (items.length === 0) {
      return (
        <p className="px-3 py-6 text-center text-sm text-gray-500">{noFeaturesFound}</p>
      );
    }
    return items.map((item) => (
      <Link
        key={item.href}
        to={item.href}
        className={`flex items-center px-3 py-2 rounded-lg text-sm font-medium ${
          isNavActive(item.href)
            ? 'bg-primary text-white'
            : 'text-gray-700 hover:bg-gray-100'
        }`}
        onClick={onNavigate}
      >
        <span className="mr-3">{item.icon}</span>
        {item.name}
      </Link>
    ));
  };

  const renderSidebarSearch = (inputId: string) => (
    <div className="shrink-0 px-4 pb-3 space-y-2">
      <label htmlFor={inputId} className="sr-only">
        {searchPlaceholder}
      </label>
      <div className="relative">
        <MagnifyingGlassIcon
          className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          aria-hidden="true"
        />
        <input
          id={inputId}
          type="search"
          value={navSearch}
          onChange={(e) => setNavSearch(e.target.value)}
          placeholder={searchPlaceholder}
          autoComplete="off"
          className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-8 pr-8 text-sm text-gray-900 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary"
        />
        {navSearch ? (
          <button
            type="button"
            onClick={() => setNavSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600"
            aria-label={t('common.clear') || 'Clear search'}
          >
            {/* <XMarkIcon className="h-4 w-4" /> */}
          </button>
        ) : null}
      </div>
      <SearchFieldScope
        fields={navSearchAllFields}
        selected={navSearchFields}
        onToggle={toggleNavSearchField}
      />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75" 
          onClick={() => setSidebarOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSidebarOpen(false);
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={t('common.closeSidebar') || 'Close sidebar'}
        />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-white h-full">
          <div className="flex h-16 shrink-0 items-center justify-between px-4">
            <AppBrand />
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              {/* <XMarkIcon className="h-6 w-6" /> */}
            </button>
          </div>
          {renderSidebarSearch('sidebar-nav-search-mobile')}
          <nav className="flex-1 min-h-0 px-4 pb-4 space-y-2 overflow-y-auto scrollbar-hide">
            {renderNavLinks(filteredNavigation, () => setSidebarOpen(false))}
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col lg:h-full">
        <div className="flex flex-col h-full bg-white border-r border-gray-200">
          <div className="flex h-16 shrink-0 items-center px-4">
            <AppBrand />
          </div>
          {renderSidebarSearch('sidebar-nav-search-desktop')}
          <nav className="flex-1 min-h-0 px-4 pb-4 space-y-2 overflow-y-auto scrollbar-hide">
            {renderNavLinks(filteredNavigation)}
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top navigation */}
        <div className="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8">
          <button
            type="button"
            className="-m-2.5 p-2.5 text-gray-700 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
            <div className="flex flex-1"></div>
            <div className="flex items-center gap-x-4 lg:gap-x-6">
              {/* Language selector */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                  <span className="mr-1">🌐</span>
                  <span>{currentLanguageName}</span>
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    {languages.map((lang) => (
                      <Menu.Item key={lang.code}>
                        {({ active }) => (
                          <button
                            onClick={() => changeLanguage(lang.code)}
                            className={`${
                              active ? 'bg-gray-100' : ''
                            } ${
                              currentLanguage === lang.code ? 'bg-blue-50 font-medium' : ''
                            } block w-full px-4 py-2 text-left text-sm text-gray-700`}
                          >
                            {lang.name}
                            {currentLanguage === lang.code && (
                              <span className="ml-2 text-blue-600">✓</span>
                            )}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>

              {/* User menu */}
              <Menu as="div" className="relative">
                <Menu.Button className="flex items-center text-sm font-medium text-gray-700 hover:text-gray-900">
                  <UserInfo showSessionInfo={true} />
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={logout}
                          className={`${
                            active ? 'bg-gray-100' : ''
                          } block w-full px-4 py-2 text-left text-sm text-gray-700`}
                        >
                          {t('auth.logout')}
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6">
          {children}
        </main>
      </div>
    </div>
  );
};
